import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  getDVIRReports,
  getDVIRTemplates,
  showDeleteReportConfirmationModal,
  setShowViewReportModal,
  toggleShowAddReportModal,
  setReportFilters,
} from "../../dvirSlice";
import { getDriversV2 } from "../../../DriversV2/driversV2Slice";
import { getVehicles } from "../../../Vehicles/vehiclesSlice";
import { DVIRReport, dvirReportStatusToText } from "../../../../models";
import "./DVIRReportsTab.scss";

const getStatusColor = (status: string): string => {
  switch (status) {
    case "completed":
      return "status-completed";
    case "created":
      return "status-pending";
    default:
      return "status-pending";
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "-";
  // Only add time component for date-only strings to prevent UTC timezone shift
  const hasTime = dateString.includes("T");
  const date = new Date(hasTime ? dateString : dateString + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export function DVIRReportsTab() {
  const dispatch = useAppDispatch();

  const reports = useAppSelector((state) => state.dvir.reports);
  const getDVIRReportsAPI = useAppSelector((state) => state.dvir.getDVIRReports);
  const deleteDVIRReportAPI = useAppSelector(
    (state) => state.dvir.deleteDVIRReport
  );
  const createDVIRReportAPI = useAppSelector(
    (state) => state.dvir.createDVIRReport
  );
  const reportFilters = useAppSelector((state) => state.dvir.reportFilters);
  const drivers = useAppSelector((state) => state.driversV2.drivers);
  const vehicles = useAppSelector((state) => state.vehicles.vehicles);
  const templates = useAppSelector((state) => state.dvir.templates);

  const [dateFrom, setDateFrom] = React.useState(reportFilters.dateFrom || "");
  const [dateTo, setDateTo] = React.useState(reportFilters.dateTo || "");
  const [statusFilter, setStatusFilter] = React.useState(
    reportFilters.status || ""
  );
  const [driverFilter, setDriverFilter] = React.useState(
    reportFilters.driverId || ""
  );
  const [vehicleFilter, setVehicleFilter] = React.useState(
    reportFilters.vehicleId || ""
  );
  const [templateFilter, setTemplateFilter] = React.useState(
    reportFilters.templateId || ""
  );

  const PAGE_SIZE = 20;
  const currentOffset = reportFilters.offset || 0;
  const currentPage = Math.floor(currentOffset / PAGE_SIZE) + 1;

  React.useEffect(() => {
    dispatch(getDVIRReports({ ...reportFilters, count: PAGE_SIZE }));
    // Load filter options
    if (drivers.length === 0) dispatch(getDriversV2());
    if (vehicles.length === 0) dispatch(getVehicles());
    if (templates.length === 0) dispatch(getDVIRTemplates());
  }, [dispatch, reportFilters, drivers.length, vehicles.length, templates.length]);

  React.useEffect(() => {
    // Refetch reports after successful delete
    if (!deleteDVIRReportAPI.loading && deleteDVIRReportAPI.value) {
      dispatch(getDVIRReports(reportFilters));
    }
  }, [deleteDVIRReportAPI.loading, deleteDVIRReportAPI.value, dispatch, reportFilters]);

  React.useEffect(() => {
    // Refetch reports after successful create
    if (!createDVIRReportAPI.loading && createDVIRReportAPI.value) {
      dispatch(getDVIRReports(reportFilters));
    }
  }, [createDVIRReportAPI.loading, createDVIRReportAPI.value, dispatch, reportFilters]);

  const handleDeleteReport = (report: DVIRReport) => {
    dispatch(showDeleteReportConfirmationModal(report));
  };

  const handleViewReport = (report: DVIRReport) => {
    dispatch(setShowViewReportModal(report));
  };

  const handleAddReport = () => {
    dispatch(toggleShowAddReportModal());
  };

  const handleApplyFilters = () => {
    const filters: any = { offset: 0 };
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    if (statusFilter) filters.status = statusFilter;
    if (driverFilter) filters.driverId = driverFilter;
    if (vehicleFilter) filters.vehicleId = vehicleFilter;
    if (templateFilter) filters.templateId = templateFilter;
    dispatch(setReportFilters(filters));
  };

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setDriverFilter("");
    setVehicleFilter("");
    setTemplateFilter("");
    dispatch(setReportFilters({ offset: 0 }));
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, currentOffset - PAGE_SIZE);
    dispatch(setReportFilters({ ...reportFilters, offset: newOffset }));
  };

  const handleNextPage = () => {
    const newOffset = currentOffset + PAGE_SIZE;
    dispatch(setReportFilters({ ...reportFilters, offset: newOffset }));
  };

  if (getDVIRReportsAPI.loading && reports.length === 0) {
    return (
      <div className="dvir-reports-tab loading">
        <div className="loading-text">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="dvir-reports-tab">
      <div className="header-filters-section">
        <div className="header-row">
          <div className="title-section">
            <h2>DVIR Reports</h2>
            <p className="subtitle">
              View and manage Driver Vehicle Inspection Reports
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleAddReport}>
            + New Report
          </button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Driver</label>
            <select
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Drivers</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.userId}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Vehicle</label>
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Vehicles</option>
              {vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Template</label>
            <select
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Templates</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Statuses</option>
              <option value="created">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="filter-actions">
            <button className="btn btn-filter" onClick={handleApplyFilters}>
              Apply
            </button>
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>No reports found</h3>
            <p>
              {Object.keys(reportFilters).length > 0
                ? "No reports match your filter criteria. Try adjusting your filters."
                : "DVIR reports will appear here when drivers complete their inspections."}
            </p>
          </div>
        </div>
      ) : (
        <div className="reports-table-container">
          <div className="table-scroll-wrapper">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Driver</th>
                  <th>Vehicle</th>
                  <th>Template</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report: DVIRReport) => (
                  <tr key={report.id}>
                    <td className="date-cell">
                      <div className="date">{formatDate(report.date)}</div>
                    </td>
                    <td className="driver-cell">
                      <div className="driver-name">{report.driverName}</div>
                    </td>
                    <td className="vehicle-cell">
                      <div className="vehicle-label">{report.vehicleLabel}</div>
                    </td>
                    <td className="template-cell">
                      <div className="template-name">{report.templateName}</div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusColor(
                          report.status
                        )}`}
                      >
                        {dvirReportStatusToText[report.status]}
                      </span>
                    </td>
                    <td className="submitted-cell">
                      {report.submittedAt ? (
                        <>
                          <div className="submitted-date">
                            {formatDate(report.submittedAt)}
                          </div>
                          <div className="submitted-time">
                            {formatTime(report.submittedAt)}
                          </div>
                        </>
                      ) : (
                        <div className="submitted-date">-</div>
                      )}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-view"
                          onClick={() => handleViewReport(report)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteReport(report)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button
              className="btn btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1 || getDVIRReportsAPI.loading}
            >
              Previous
            </button>
            <span className="page-info">Page {currentPage}</span>
            <button
              className="btn btn-secondary"
              onClick={handleNextPage}
              disabled={reports.length < PAGE_SIZE || getDVIRReportsAPI.loading}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
